/**
 * Gold "PRO / TRIAL / FOUNDER" badge.
 * Shows for any active paid or trial tier.
 *
 * Variants:
 *  - "TRIAL"   → gold with a clock icon (trial users)
 *  - "PRO"     → gold pill (monthly / annual)
 *  - "FOUNDER" → gold pill with crown (lifetime)
 *
 * Two size variants: "sm" (navbar pill) | "md" (profile dropdown)
 */
import { Crown, Clock } from "lucide-react";
import { useSubscription } from "../lib/SubscriptionContext";

const PAID_TIERS = new Set(["trial", "pro_monthly", "pro_annual", "founder"]);

export default function GoldBadge({ size = "sm", className = "" }) {
  const { tier } = useSubscription();
  if (!PAID_TIERS.has(tier)) return null;

  const isFounder = tier === "founder";
  const isTrial   = tier === "trial";
  const label     = isFounder ? "FOUNDER" : isTrial ? "TRIAL" : "PRO";

  const sizeClasses = size === "md"
    ? "px-2 py-0.5 text-[11px]"
    : "px-1.5 py-0.5 text-[9px] leading-none";

  return (
    <span
      data-testid={`gold-badge-${size}`}
      className={
        "inline-flex items-center gap-1 rounded-full font-bold tracking-wider "
        + "border border-amber-400/40 bg-gradient-to-br from-amber-300/15 to-amber-500/5 "
        + "text-amber-300 shadow-[0_0_8px_-2px_rgba(251,191,36,0.4)] "
        + sizeClasses + " " + className
      }
      title={
        isFounder ? "Filmistani Founder · Lifetime"
        : isTrial  ? "Filmistani Pro · 7-day Trial"
        : "Filmistani Pro"
      }
    >
      {isFounder && <Crown className="w-3 h-3" />}
      {isTrial   && <Clock className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}
